import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { ApiServiceAdmin } from '../services/ApiServiceAdmin';

export interface SchoolYear {
  id: number;
  label: string;
  academic_year?: string;
  start_date?: string;
  end_date?: string;
  status: 'active' | 'closed' | string;
  is_current?: boolean;
  is_open_for_inscription?: boolean;
  closed_at?: string | null;
}

interface SchoolYearContextType {
  years: SchoolYear[];
  selectedYear: SchoolYear | null;
  currentYear: SchoolYear | null;
  inscriptionYear: SchoolYear | null;
  loading: boolean;
  isReadOnly: boolean;
  selectSchoolYear: (year: SchoolYear) => void;
  refreshYears: () => Promise<void>;
  setInscriptionYear: (year: SchoolYear) => Promise<{ success: boolean; error?: string }>;
  setCurrentSchoolYear: (year: SchoolYear) => Promise<{ success: boolean; error?: string }>;
  createSchoolYear: (opts?: {
    label?: string;
    start_date?: string;
    end_date?: string;
    copy_from_school_year_id?: number;
    set_as_current?: boolean;
  }) => Promise<{ success: boolean; error?: string }>;
  closeSchoolYear: (
    closeId: number,
    activateSchoolYearId: number
  ) => Promise<{ success: boolean; error?: string }>;
}

const STORAGE_KEY = 'finance_selected_school_year_id';

const SchoolYearContext = createContext<SchoolYearContextType | null>(null);

export function SchoolYearProvider({ children }: { children: ReactNode }) {
  const [years, setYears] = useState<SchoolYear[]>([]);
  const [selectedYear, setSelectedYear] = useState<SchoolYear | null>(null);
  const [currentYear, setCurrentYear] = useState<SchoolYear | null>(null);
  const [inscriptionYear, setInscriptionYearState] = useState<SchoolYear | null>(null);
  const [loading, setLoading] = useState(true);
  const api = ApiServiceAdmin.getInstance();

  const applySelection = useCallback(
    (year: SchoolYear | null) => {
      setSelectedYear(year);
      if (year) {
        localStorage.setItem(STORAGE_KEY, String(year.id));
        api.setFinanceSchoolYear({ id: year.id, label: year.label });
      } else {
        localStorage.removeItem(STORAGE_KEY);
        api.setFinanceSchoolYear(null);
      }
    },
    [api]
  );

  const refreshYears = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSchoolYears();
      if (!res.success) return;
      const list: SchoolYear[] = res.data || [];
      setYears(list);
      const cur =
        res.currentSchoolYear ||
        list.find((y) => y.is_current) ||
        list[0] ||
        null;
      setCurrentYear(cur);
      // Année ouverte aux inscriptions (flag is_open_for_inscription)
      const inscYear = list.find((y) => y.is_open_for_inscription) || cur;
      setInscriptionYearState(inscYear ?? null);

      const storedId = localStorage.getItem(STORAGE_KEY);
      const fromStorage = storedId
        ? list.find((y) => String(y.id) === storedId)
        : null;
      applySelection(fromStorage || cur);
    } finally {
      setLoading(false);
    }
  }, [api, applySelection]);

  useEffect(() => {
    refreshYears();
  }, [refreshYears]);

  const selectSchoolYear = useCallback(
    (year: SchoolYear) => {
      applySelection(year);
    },
    [applySelection]
  );

  const setInscriptionYear = useCallback(
    async (year: SchoolYear) => {
      const res = await api.setInscriptionSchoolYear(year.id);
      if (!res.success) return { success: false, error: res.error };
      setInscriptionYearState(year);
      await refreshYears();
      return { success: true };
    },
    [api, refreshYears]
  );

  const setCurrentSchoolYear = useCallback(
    async (year: SchoolYear) => {
      const res = await api.setCurrentSchoolYear(year.id);
      if (!res.success) return { success: false, error: res.error };
      await refreshYears();
      return { success: true };
    },
    [api, refreshYears]
  );

  const closeSchoolYear = useCallback(
    async (closeId: number, activateSchoolYearId: number) => {
      const res = await api.closeSchoolYear(closeId, activateSchoolYearId);
      if (!res.success) {
        return { success: false, error: res.error };
      }
      const activated = res.activated as SchoolYear | undefined;
      await refreshYears();
      if (activated?.id) {
        applySelection(activated);
      }
      return { success: true };
    },
    [api, refreshYears, applySelection]
  );

  const createSchoolYear = useCallback(
    async (opts?: {
      label?: string;
      start_date?: string;
      end_date?: string;
      copy_from_school_year_id?: number;
      set_as_current?: boolean;
    }) => {
      const res = await api.createSchoolYear({
        ...opts,
        copy_from_school_year_id:
          opts?.copy_from_school_year_id ?? selectedYear?.id ?? currentYear?.id,
      });
      if (!res.success) {
        return { success: false, error: res.error };
      }
      const created = res.data as SchoolYear | undefined;
      await refreshYears();
      if (created?.id) {
        applySelection(created);
      }
      return { success: true };
    },
    [api, refreshYears, selectedYear, currentYear, applySelection]
  );

  const isReadOnly = selectedYear?.status === 'closed';

  const value = useMemo(
    () => ({
      years,
      selectedYear,
      currentYear,
      inscriptionYear,
      loading,
      isReadOnly,
      selectSchoolYear,
      refreshYears,
      setInscriptionYear,
      setCurrentSchoolYear,
      createSchoolYear,
      closeSchoolYear,
    }),
    [
      years,
      selectedYear,
      currentYear,
      inscriptionYear,
      loading,
      isReadOnly,
      selectSchoolYear,
      refreshYears,
      setInscriptionYear,
      setCurrentSchoolYear,
      createSchoolYear,
      closeSchoolYear,
    ]
  );

  return (
    <SchoolYearContext.Provider value={value}>{children}</SchoolYearContext.Provider>
  );
}

export function useSchoolYear(): SchoolYearContextType {
  const ctx = useContext(SchoolYearContext);
  if (!ctx) {
    throw new Error('useSchoolYear doit être utilisé dans SchoolYearProvider');
  }
  return ctx;
}
